<script lang="ts">
  import { Download, Lock, MessagesSquare } from 'lucide-svelte';
  import { shareApi } from '$lib/api/share';
  import { getErrorMessage } from '$lib/api/client';
  import { formatMoney } from '$lib/utils/money';
  import { avatarColor, initials, relativeAge } from '$lib/utils/display';
  import { QUOTE_VERTICAL_META } from '$lib/types';
  import type { SharedQuotation } from '$lib/types';

  const { token }: { token: string } = $props();

  type ViewPhase = 'loading' | 'password' | 'not_found' | 'ready';

  let phase = $state<ViewPhase>('loading');
  let quote = $state<SharedQuotation | null>(null);
  let password = $state('');
  let passwordAttempt = $state('');
  let passwordError = $state('');
  let downloading = $state(false);

  let draftName = $state('');
  let draftEmail = $state('');
  let draftBody = $state('');
  let posting = $state(false);
  let postError = $state('');

  async function load(pw: string) {
    try {
      const result = await shareApi.get(token, pw);
      if (result.status === 'ok') {
        quote = result.quote;
        password = pw;
        phase = 'ready';
      } else if (result.status === 'password_required') {
        phase = 'password';
        if (pw) passwordError = 'Incorrect password. Please try again.';
      } else {
        phase = 'not_found';
      }
    } catch (err) {
      passwordError = getErrorMessage(err);
      phase = 'not_found';
    }
  }

  load('');

  function submitPassword(e: Event) {
    e.preventDefault();
    passwordError = '';
    load(passwordAttempt);
  }

  async function downloadPdf() {
    if (!quote) return;
    downloading = true;
    try {
      await shareApi.downloadPdf(token, password, quote.formatted_number);
    } catch (err) {
      postError = getErrorMessage(err);
    } finally {
      downloading = false;
    }
  }

  async function postComment(e: Event) {
    e.preventDefault();
    if (!draftName.trim() || !draftEmail.trim() || !draftBody.trim()) return;
    posting = true;
    postError = '';
    try {
      const comment = await shareApi.postComment(token, password, {
        author_name: draftName.trim(),
        author_email: draftEmail.trim(),
        body: draftBody.trim()
      });
      if (quote) quote = { ...quote, comments: [...quote.comments, comment] };
      draftBody = '';
    } catch (err) {
      postError = getErrorMessage(err);
    } finally {
      posting = false;
    }
  }
</script>

<div class="min-h-screen bg-toyama-light-carbon font-body text-toyama-heavy-carbon">
  {#if phase === 'loading'}
    <p class="tym-loading-state p-10">Loading&hellip;</p>
  {:else if phase === 'not_found'}
    <div class="mx-auto max-w-md px-6 py-24 text-center">
      <h1 class="font-heading text-xl font-bold text-navy">Link not available</h1>
      <p class="mt-2 text-[13.5px] text-dark-gray">
        This share link is invalid, has been revoked, or the quotation is no longer approved.
      </p>
    </div>
  {:else if phase === 'password'}
    <div class="mx-auto max-w-sm px-6 py-24">
      <div class="tym-card p-6 text-center">
        <Lock class="mx-auto mb-3 h-8 w-8 text-toyama-teal" />
        <h1 class="font-heading text-lg font-bold text-navy">Password protected</h1>
        <p class="mt-1 mb-4 text-[13px] text-dark-gray">Enter the password shared with you to view this quotation.</p>
        <form onsubmit={submitPassword} class="flex flex-col gap-2.5">
          <input type="password" class="input" placeholder="Password" bind:value={passwordAttempt} />
          {#if passwordError}<p class="tym-error-state text-left text-xs">{passwordError}</p>{/if}
          <button type="submit" class="btn-primary justify-center">View quotation</button>
        </form>
      </div>
    </div>
  {:else if quote}
    <div class="border-b border-toyama-cloudy-gray bg-[var(--tym-white)]">
      <div class="mx-auto flex max-w-5xl items-center justify-between px-6 py-6">
        <div>
          <p class="text-xs text-dark-gray">Toyama Controls &amp; Systems Pvt Ltd</p>
          <h1 class="font-heading text-2xl text-navy">Quotation {quote.formatted_number}</h1>
          <span
            class="mt-1 inline-block rounded-[5px] bg-toyama-teal/10 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[.04em] text-toyama-teal"
          >
            {QUOTE_VERTICAL_META[quote.vertical]?.label ?? quote.vertical}
          </span>
        </div>
        <button type="button" class="btn-primary" disabled={downloading} onclick={downloadPdf}>
          <Download class="h-4 w-4" /> Download PDF
        </button>
      </div>
    </div>

    <div class="mx-auto max-w-5xl px-6 py-8">
      <div class="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div class="tym-card p-5">
          <h2 class="mb-3 font-heading text-[15px] font-bold text-navy">Client information</h2>
          <div class="flex flex-col gap-2 text-[13px]">
            <div><span class="text-dark-gray">Client</span><p class="font-medium text-navy">{quote.client_name}</p></div>
            {#if quote.project_name}
              <div><span class="text-dark-gray">Project</span><p class="font-medium text-navy">{quote.project_name}</p></div>
            {/if}
            {#if quote.project_location}
              <div><span class="text-dark-gray">Location</span><p class="font-medium text-navy">{quote.project_location}</p></div>
            {/if}
          </div>
        </div>
        <div class="tym-card p-5">
          <h2 class="mb-3 font-heading text-[15px] font-bold text-navy">Quotation details</h2>
          <div class="flex flex-col gap-2 text-[13px]">
            <div><span class="text-dark-gray">Date</span><p class="font-medium text-navy">{new Date(quote.created_at).toLocaleDateString()}</p></div>
            <div><span class="text-dark-gray">Currency</span><p class="font-medium text-navy">{quote.currency}</p></div>
            {#if quote.delivery_terms}
              <div><span class="text-dark-gray">Delivery terms</span><p class="font-medium text-navy">{quote.delivery_terms}</p></div>
            {/if}
          </div>
        </div>
      </div>

      {#each quote.sections as section (section.id)}
        <div class="tym-card mb-4 overflow-hidden p-0">
          <div class="border-b border-toyama-cloudy-gray bg-toyama-light-carbon px-5 py-3">
            <span class="font-heading text-[14px] font-semibold text-navy">{section.name}</span>
            {#if section.quantity > 1}<span class="text-xs text-dark-gray"> (Qty: {section.quantity})</span>{/if}
          </div>
          <table class="w-full text-[13px]">
            <thead>
              <tr class="border-b border-toyama-cloudy-gray text-left text-[11px] uppercase text-dark-gray">
                <th class="px-5 py-2 font-semibold">Item</th>
                <th class="px-5 py-2 font-semibold">Part No.</th>
                <th class="px-5 py-2 text-center font-semibold">Qty</th>
                <th class="px-5 py-2 text-right font-semibold">Unit price</th>
                <th class="px-5 py-2 text-right font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {#each section.line_items as item (item.id)}
                <tr class="border-b border-toyama-cloudy-gray/60 last:border-0">
                  <td class="px-5 py-2.5">{item.description}</td>
                  <td class="px-5 py-2.5 text-xs text-dark-gray">{item.part_number}</td>
                  <td class="px-5 py-2.5 text-center">{item.quantity}</td>
                  <td class="px-5 py-2.5 text-right">{formatMoney(item.unit_price_minor, quote.currency)}</td>
                  <td class="px-5 py-2.5 text-right font-medium">{formatMoney(item.line_total_minor, quote.currency)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/each}

      <div class="tym-card mb-8 ml-auto max-w-sm p-5">
        <div class="flex flex-col gap-2 text-[13.5px]">
          <div class="flex justify-between"><span class="text-dark-gray">Subtotal</span><span>{formatMoney(quote.subtotal_minor, quote.currency)}</span></div>
          <div class="flex justify-between"><span class="text-dark-gray">Tax ({(quote.tax_rate_bps / 100).toFixed(2)}%)</span><span>{formatMoney(quote.tax_amount_minor, quote.currency)}</span></div>
          <div class="mt-1.5 flex justify-between border-t-2 border-navy pt-2.5">
            <span class="font-heading font-bold text-navy">Grand total</span>
            <span class="font-heading text-[19px] font-bold text-toyama-teal">{formatMoney(quote.grand_total_minor, quote.currency)}</span>
          </div>
        </div>
      </div>

      {#if quote.terms.length > 0}
        <div class="tym-card mb-8 p-5">
          <h2 class="mb-3 font-heading text-[15px] font-bold text-navy">Terms &amp; conditions</h2>
          <div class="flex flex-col gap-3 text-[13px]">
            {#each quote.terms as term (term.id)}
              <div>
                <p class="font-semibold text-navy">{term.title}</p>
                <p class="whitespace-pre-line text-dark-gray">{term.body}</p>
              </div>
            {/each}
          </div>
        </div>
      {/if}

      <div class="tym-card p-5">
        <div class="mb-3.5 flex items-center gap-2">
          <MessagesSquare class="h-4 w-4 text-toyama-teal" />
          <span class="font-heading text-[15px] font-bold text-navy">Comments</span>
        </div>
        <div class="mb-4 flex flex-col gap-3">
          {#each quote.comments as m (m.id)}
            <div class="flex gap-2.5">
              <span
                class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white"
                style="background:{avatarColor(m.author_name)}"
              >
                {initials(m.author_name)}
              </span>
              <div class="min-w-0">
                <div class="flex items-baseline gap-2">
                  <span class="text-[12.5px] font-semibold text-navy">{m.author_name}</span>
                  <span class="text-[11px] text-dark-gray">{relativeAge(m.created_at)}</span>
                </div>
                <div class="mt-0.5 text-[13px] leading-[1.45] text-toyama-heavy-carbon">{m.body}</div>
              </div>
            </div>
          {:else}
            <p class="text-[12.5px] text-dark-gray">No comments yet.</p>
          {/each}
        </div>
        <form onsubmit={postComment} class="flex flex-col gap-2">
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <input class="input" placeholder="Your name" bind:value={draftName} required />
            <input type="email" class="input" placeholder="Your email" bind:value={draftEmail} required />
          </div>
          <textarea
            class="pq-in w-full resize-none rounded-[9px] border border-toyama-cloudy-gray px-[11px] py-[9px] text-[13px] outline-none"
            rows="3"
            placeholder="Leave a comment…"
            bind:value={draftBody}
            required
          ></textarea>
          {#if postError}<p class="tym-error-state text-xs">{postError}</p>{/if}
          <button type="submit" class="btn-primary self-start" disabled={posting}>Post comment</button>
        </form>
      </div>
    </div>
  {/if}
</div>
